import { writeFileSync, existsSync, readFileSync, mkdirSync } from 'node:fs'
import { resolve } from 'node:path'

const MIRROR_BASE = 'https://raw.githubusercontent.com/driftless-sh/driftless-skill/main/skills/driftless'
const SKILL_URL = `${MIRROR_BASE}/SKILL.md`
const REFERENCE_FILES = [
  'commands.md',
  'retrieve.md',
  'broker.md',
  'workflow.md',
  'cloud.md',
  'topic-anatomy.md',
  'troubleshooting.md',
]
const TEMPLATE_FILES = [
  'reference.md',
  'decision.md',
  'runbook.md',
]

const DRIFTLESS_START = '<!-- driftless:start -->'
const DRIFTLESS_END = '<!-- driftless:end -->'

const AGENTS_BODY = `## Driftless — Team Memory

Driftless is the team's shared memory in Cloud — a vault you READ before you work and WRITE clean notes into as you learn. Topics are durable, area-filed, code-anchored notes (what / how / gotchas / decisions / invariants). Most of what you write stays a Note (that's healthy); a few become Knowledge (merged in on an owner/admin's say-so — you can run the merge, but only when one explicitly asks). There is no approval gate you must clear, so quality is enforced AT WRITE TIME — by the six rules below. On a PR, the Auditor reviews the change against recorded context and comments only when it has a finding; it informs, never blocks — no coverage score, no homework.

### The two moves
1. READ before you work — pull what the team already knows for the area you're about to touch. Drifted topics carry a freshness badge inline.
2. WRITE one clean note after you learn something durable. A Note is first-class — the agent reads it (Knowledge as truth, a Note as a hint). Write it clean; there's no gate to fix it later.

### THE SIX RULES every write follows (this is the gate)

- A. ONE concept per note — tempted to cover two? write two.
- B. ALWAYS file into an area (--area) — never leave it Unassigned, that's where vaults rot.
- C. Anchor NARROW (--pattern, ~5-40 files, never src/**) — for non-code, anchor the doc/system it's about.
- D. Trust is a SIGNAL, not a gate — Knowledge is truth, a Note is a hint; both are real. Write a good note, don't chase approval.
- E. Rewrite to consolidate; append to add — never stack a wall (redundancy / contradiction / changelog).
- F. Content is the durable WHY, not a changelog — no dates, "shipped", or PR/commit refs (that lives in git).

Litmus for whether something deserves a note at all: would a future code change meaningfully CONTRADICT it? If not, it's not a topic.

### Default behavior for Driftless work

When the user invokes Driftless, asks about workspace context, or asks you to operate any Driftless plane, do not guess. Use this AGENTS.md routing table first. If the request involves a surface you do not clearly know how to operate — Topics/Knowledge, Notes, Projects, Collections/Records, Entities, Broker/integrations, workspaces, areas, tags, hooks, sync, or pre-commit refresh — read \`@.driftless/skill.md\` completely before acting, then follow its referenced files only as needed.

Do not ask the user to "load the Driftless skill." In this repo, \`@.driftless/skill.md\` is the local operational manual and AGENTS.md points to it. Load it yourself when the task is Driftless-specific or when the command surface is uncertain.

### Routing — first move per situation

| Situation                                  | First command                                         |
|--------------------------------------------|-------------------------------------------------------|
| Empty workspace / few topics yet           | driftless login → install-skill → just work, persist what you learn |
| About to edit specific files               | driftless context get --files "a.ts,b.ts"  (drift shows inline) |
| Have a TASK but no slug yet                 | driftless_context_retrieve { task, files }  (MCP) / POST /topics/retrieve — ranked, brief-by-default; START HERE |
| Need context for an area                   | driftless context search <kw> ; context get <slug>    |
| Working a project board                    | driftless project card next <pid>  → load context_bundle → work → validate → mark done |
| Acting on a collection record              | driftless_collection action:'retrieve' id:<cid>  (records + the criterion Knowledge to read FIRST) |
| Operating a connected integration          | driftless broker operations <provider> → broker invoke <provider> <op> (operation missing → STOP + report) |
| Learned something durable                  | driftless context add/update <slug> --area <domain> ... |
| About to commit                            | driftless context get --diff  (add --mark to flag matched topics drifted) |
| Context itself looks untrustworthy         | driftless context doctor                              |

Retrieve-first: when you know the FILES, \`context get --files\`; when you have a TASK but no slug, \`driftless_context_retrieve\` (it ranks search + match-files + list, drifted-first). Both default to BRIEF bodies (the durable why, not full content) — \`context get <slug>\` for the one full body you actually need. Don't hand-chain search→list→get when retrieve does it in one call.

\`driftless sync\` is an optional team-wide digest — you almost never need it, and never to start; drift already reaches you as a freshness badge when you \`context get\` the area you're working on.

### Read — BEFORE editing code

  driftless context get <slug>                                # known topic
  driftless context search <keyword>                          # discover topic
  driftless context list --area <name>                        # list a whole domain
  driftless context get --files "src/billing/billing.service.ts,src/billing/refunds/*"

Returns every topic whose anchors (patterns or where_files) match any of the given paths. Read what / how / gotchas / decisions / invariants / history before editing. After time away, check history first — files may have moved, context may have been updated by another agent.

No topic matches the file? Read the code, then create a topic before assuming. Absence of a topic is a gap to close, not permission to skip.

### Write — AFTER you learn (clean note: the six rules; never clobber Knowledge)

Write the topic as markdown --content. The content body IS the topic: a clear, readable explanation, like a good doc. Keep --what to one tight sentence; the structured fields (--gotcha / --decision / --invariant / --check) are OPTIONAL highlights — add one only to surface that specific thing, never to fill a form. ALWAYS set --area (rule B) and a narrow --pattern (rule C).

If topic exists — batch every related change into ONE update (each PATCH bumps version + writes history):

  driftless context update <slug> \\
    --content @notes.md \\
    --gotcha "..." \\
    --decision "..." \\
    --add-pattern "src/billing/refunds/**" \\
    --rel depends_on:other-topic

Append vs rewrite — pick by intent. Append a genuinely NEW standalone fact (--gotcha/--decision; atomic, concurrency-safe). When you are CORRECTING, refining, or a field already overlaps/contradicts/has gone stale, REWRITE it instead: context get -> fold new into old -> update --content (or --decisions/--gotchas to replace one field). Pull fresh first (rewrite is read-modify-write). Append grows a topic; rewrite keeps it true — together a field can never rot into a wall. decisions/gotchas/invariants hold the durable WHY a code change would contradict — never status, changelog, "shipped/TODO", dates, or commit refs (that lives in git).

If no topic exists — write the content, file it in an area, anchor it:

  driftless context add <slug> --title "Refunds" \\
    --content @.driftless/assets/templates/reference.md \\
    --area billing \\
    --pattern "src/billing/refunds/**"

Naming (do not skip): \`<slug>\` = the kebab id (specific). \`--title\` = a SHORT human label, one or two words like a diagram node (MCP, Billing, Auth) — never a sentence. The explanation goes in \`--what\` (one line) + \`--content\`, never in the title. Omitting --title defaults it to the slug, which reads badly — always set a short --title.

Area vs tags — don't confuse them. \`--area\` is the topic's HOME (one domain: Billing, Auth, Infra) — always set it. \`--tags\` are OPTIONAL transversal cross-cuts that group topics across DIFFERENT subjects (security, performance, tech-debt) — NOT the subject (never tag "billing" on a billing topic) and NOT the home. Most notes need zero or one tag; reuse the registry (\`driftless tags\`) before coining one.

Every topic lives in the workspace and is visible to the team. Starter templates shipped: reference, decision, runbook (scaffolding — the content is what matters, not the form).

Secret hygiene: a write is blocked if a field looks like a credential (API key, token, private key) — topics are team-visible and not encrypted at rest, so never paste a live secret. If a block is a false positive, override with \`--allow-secrets\` (CLI) / \`allow_secrets: true\` (MCP).

Pattern validation at write time. The CLI globs every --pattern / --add-pattern against your local checkout BEFORE the API call:
  ✓ pattern matches N files — shown with sample paths
  ⚠ over-broad (> 100 files)               — split into narrower topics
  ⚠ matches only node_modules/dist/.git    — likely unintended; fix the glob
  ✗ matches 0 files                        — BLOCKING; create/update aborted

Anchoring discipline: an anchor is a CONTRACT — it declares which boundary (one service/module: a
subtree in a monorepo, or a repo) a topic governs, and that contract is what makes drift + delivery
precise. 5–40 matches healthy; 100+ is a catch-all trap. Anchor to ONE boundary; multi-anchor when a
concept spans services, never a wide glob. A whole domain is a HUB + per-service SPOKES, not one
topic: don't write api-backend --pattern "apps/api/**"; write a light api-backend hub linked
(--rel documents:billing-service) to narrow spokes (billing-service -> apps/api/billing/**). Each
spoke drifts only when its service changes.

  driftless context update <slug> --add-pattern "<glob>"    # idempotent
  driftless context update <slug> --remove-pattern "<glob>" # idempotent

Cross-repo (same concept lives here too):
  driftless context link <slug>

Link related topics with [[slug]] inside any free-text field — both directions surface on get / dashboard.
Strong typed edges with --rel: relates_to, depends_on, supersedes, blocks, implements, documents, risk_for.

Full topic anatomy → .driftless/references/topic-anatomy.md

### UC0 — First-time setup (~60 seconds)

  driftless login                                                # auth
  driftless install-skill                                        # writes CLAUDE.md, AGENTS.md, .driftless/
  driftless context add <real-concept> \\                         # first topic = something REAL you know
    --what "..." --gotcha "..." --pattern "src/<module>/**"       # (no generic placeholders — they rot)

There is no code parser to run. Topics grow from one habit: you persist what you learn after each session (UC2). The value compounds — with a couple of topics there's little to pull; from a handful onward, retrieval before editing pays off every time, for you and for a teammate's agent.

### Governance — most notes stay notes (that's fine)

You write Notes. Most notes stay notes — that's expected; promotion to Knowledge is for the few cross-cutting truths the whole team relies on, NOT a step you must complete on every note. Merging a note into Knowledge is an owner/admin act: you can run the merge, but ONLY when an owner/admin explicitly asks — never on your own initiative. To change existing Knowledge, open a Suggested edit — an owner/admin merges it.

Note (your draft) → Knowledge (the team's source of truth — the notes merged in). The read response carries governance.authoritative — treat Knowledge as TRUTH, a Note as a HINT. Don't chase approval; just leave clean notes (the six rules).

  driftless context propose <slug>     # put a note up for review
  driftless context approve <slug>     # merge a note into the team's Knowledge (owner/admin only; run it only when asked)
  driftless context reject <slug>      # send it back, with feedback
  driftless context archive <slug>     # retire it

Don't clobber Knowledge — open a Suggested edit (a content change an owner/admin reviews and merges):

  driftless context pr <slug>                                  # list suggested edits
  driftless context pr <slug> --open --summary "why" --content @file
  driftless context pr <slug> --merge <id>                     # merge into Knowledge (owner/admin)

If a topic is already Knowledge and your finding changes it, open a Suggested edit (\`pr\`) instead of overwriting. approve/merge need owner/admin authority — you write notes and propose; you run the merge only when an owner/admin explicitly tells you to, never unprompted.

### Workspaces — the container you operate in

A workspace is the top-level container for context; you may belong to several. One login registers all of them under one key (scoped credentials), so you switch without re-authenticating:

  driftless workspace list                # the workspaces you belong to (● = active)
  driftless workspace switch <slug>       # operate on another one (no re-login)
  driftless context move <slug> --to <ws> # re-home a topic to another workspace (re-review there)

A topic lives in exactly one workspace. \`move\` resets it to a Note in its new home (approval doesn't carry across workspaces) and needs owner/admin in the source + membership in the target.

### Working a project board — the agent loop

A project is a board of cards; each card is one unit of work with an optional \`acceptance\` criterion and \`validate\` command. Walk it in a loop — repeat until \`project_done\`:

  driftless project card next <project-id>   # 1. next ready card + its context_bundle (one call)
  #    -> { card, context_bundle, project_done }   MCP: driftless_project_card action:'next'
  # 2. Work the card. Load context_bundle FIRST — it carries the team's recorded memory for
  #    that card's area, so you don't re-derive what the team already knows.
  # 3. Validate — run the card's \`validate\` command if present. Non-zero = stop-and-fix, do
  #    NOT mark done. Check \`acceptance\` to confirm the outcome matches the definition of done.
  driftless context update <slug> --gotcha "..."          # 4. write-back anything durable you learned
  driftless project card status <project-id> <card-id> done   #    then mark done and ask for next

Dep-gating: a \`todo\` card with unmet deps never appears in \`next\` — it goes "ready" only once every card in its \`depends_on\` is \`done\`. No ready cards and none in-flight → \`project_done\`. If a human decision blocks you, set the card \`blocked\` instead of guessing.

Mid-loop write-back: a gotcha you hit while working a card → \`driftless note add --content "..." --project <pid> --card <card-id>\`. It attaches to that card, rides its next context_bundle, and is synthesized into a draft topic at project close — a learning from card 3 is never lost waiting for the end.

### Collections — acting on operational records

A Collection (CRM, tracker, content calendar) holds typed Records. Before you act on a record, read the collection's CRITERION — the team's "how we do this" (criterion_rel_slugs resolved to topics). The retrieve action delivers both in one call:

  driftless_collection action:'retrieve' id:'<collection-id>'   # → { records, nextCursor, criterion, criterion_missing }
  # read the criterion, THEN act:
  driftless_collection_record action:'update' collection_id:'<id>' record_id:'<id>' fields:{...}

\`action:'context'\` returns just the criterion (no records). A record's status is validated against its Collection's own stages. Collections are a SEPARATE primitive from projects — they coexist.

When operating from a terminal/CLI instead of MCP, use the CLI surface directly:

  driftless collection list --status active --json
  driftless collection get <collection-id> --json
  driftless collection records <collection-id> [--status <stage>] --json
  driftless collection record update <collection-id> <record-id> --status <stage> --json
  driftless collection record update <collection-id> <record-id> --fields '<json>' --json

If the user mentions a pipeline, hiring, candidates, bugs, CRM, records, or collection data, start with \`driftless collection list --status active --json\`, find the named Collection, inspect it with \`get\`, then list/update records. For the recruiting pipeline, look for the active Collection named \`Hiring\`. Do not ask the user to load the Driftless skill before doing this; use the CLI/MCP surface already available.

### Integrations & the broker — SETUP vs EXECUTION (and the no-scripting rule)

Operating a provider that's ALREADY connected = broker EXECUTION:

  driftless broker operations <provider>                 # the named operations this connection can do
  driftless broker invoke <provider> <operation> --input '{...}'   # run one (inline + audited)
  driftless broker records <provider> --model <m>        # read a synced model's records (delta-aware)

Credentials resolve server-side (never reach you); every call is audited. Operations come from materialized metadata — \`--refresh\` re-pulls from Nango.

CONNECTING a provider is integration SETUP — a separate, human-led concern (\`driftless integration connect <provider>\` → \`confirm\`, or the dashboard: Settings → Connections). The broker NEVER connects, disconnects, or configures a provider.

DO NOT author or deploy integration scripts. If the operation you need is NOT in \`broker operations <provider>\`, STOP and report the missing capability — never write a Nango action or any script to fill the gap. That lane is human-only.

### UC3 — Pre-commit refresh

  driftless context get --diff            # topics matching your local uncommitted changes
  driftless context update <slug> ...     # refresh any topic that drifted before pushing

### What the PR bot does

On a PR, the Auditor posts ONE comment only when it has a finding: the finding plus the gotchas, decisions and invariants the team recorded for the affected topics, delivered to whoever reviews. Stale topics are flagged inside it. A clean PR gets no comment — context arrives anchored to a real signal, never as anchor-match spam. If your change altered how an area works, update the topic (UC2) so the note stays true.

### Auto-pull (experimental, Claude Code)

\`install-skill\` already registered the context hooks (read-before + write-after). They stay inert until a workspace admin turns them ON at the dashboard (Settings → Automations). Once on: (read) before every edit Claude Code auto-injects the team's Knowledge for that file — UC1 without remembering, Notes never, deduped per session; (write) it observes which files you touch (PostToolUse) and, at session end (Stop), if you worked in a source area with NO recorded context, nudges you once to persist what you learned — with a suggested anchor from the files you actually edited. \`driftless hooks disable\` removes the hooks from this machine.

### DO NOT

- Do NOT create catch-all topics (--pattern "src/**"). One topic per concept; multi-anchor.
- Do NOT write a note without an --area — Unassigned is where vaults rot (rule B).
- Do NOT tag a topic with its own subject (--tags billing on a billing topic) — that's the --area; tags are cross-cuts.
- Do NOT split a discovery into N updates — every PATCH bumps version + writes history. Batch.
- Do NOT keep appending into a field that already overlaps or contradicts itself — rewrite it coherently (context get -> integrate -> update --content/--decisions).
- Do NOT put status/changelog/dates/commit refs in decisions/gotchas/invariants — those hold the durable why, not history. That lives in git.
- Do NOT force a 0-match pattern. The validator means it — fix the glob or use --where.
- Do NOT hand-chain search→list→get when you have a task but no slug — driftless_context_retrieve does it in one ranked call.
- Do NOT author or deploy integration scripts (Nango actions). If a broker operation is missing, STOP and report it — that lane is human-only.
- Do NOT use the broker to connect/disconnect a provider — that's integration SETUP (driftless integration / the dashboard), a separate human-led concern.
- Do NOT keep what you learned in conversation memory — persist it after the session (UC2), or it's lost.

Full skill body: @.driftless/skill.md   (Claude Code auto-loads)
Source of truth: https://github.com/driftless-sh/driftless-skill`

const AGENTS_BLOCK = `${DRIFTLESS_START}\n${AGENTS_BODY}\n${DRIFTLESS_END}\n`

async function fetchUrl(u: string, timeoutMs = 10000): Promise<Buffer> {
  const { default: https } = await import('node:https')
  const { default: http } = await import('node:http')
  const client = new URL(u).protocol === 'https:' ? https : http
  return new Promise<Buffer>((resolveP, rejectP) => {
    const req = client.get(u, (res) => {
      if (res.statusCode !== 200) {
        res.resume()
        rejectP(new Error(`HTTP ${res.statusCode}`))
        return
      }
      const chunks: Buffer[] = []
      res.on('data', (c: Buffer) => chunks.push(c))
      res.on('end', () => resolveP(Buffer.concat(chunks)))
      res.on('error', rejectP)
    })
    req.on('error', rejectP)
    req.setTimeout(timeoutMs, () => req.destroy(new Error('request timed out')))
  })
}

async function downloadSkill(cwd: string): Promise<void> {
  const driftlessDir = resolve(cwd, '.driftless')
  const refsDir = resolve(driftlessDir, 'references')
  const templatesDir = resolve(driftlessDir, 'assets', 'templates')
  if (!existsSync(driftlessDir)) mkdirSync(driftlessDir, { recursive: true })
  if (!existsSync(refsDir)) mkdirSync(refsDir, { recursive: true })
  if (!existsSync(templatesDir)) mkdirSync(templatesDir, { recursive: true })

  // Skill is critical. On failure keep a pointer placeholder so a
  // CLAUDE.md `@.driftless/skill.md` reference still resolves to something.
  try {
    writeFileSync(resolve(driftlessDir, 'skill.md'), await fetchUrl(SKILL_URL), 'utf-8')
  } catch {
    writeFileSync(
      resolve(driftlessDir, 'skill.md'),
      `# Driftless Skill\n\nSee https://github.com/driftless-sh/driftless-skill for the full skill.\n`,
      'utf-8',
    )
  }

  // References + templates are best-effort and parallel. SKILL.md links to
  // references/*.md and assets/templates/*.md (relative); without these on
  // disk those links are dead locally. Per-file failure is isolated — one
  // missing file must never break onboarding. Re-fetched every run (self-heal).
  await Promise.allSettled([
    ...REFERENCE_FILES.map(async (name) => {
      try {
        const buf = await fetchUrl(`${MIRROR_BASE}/references/${name}`)
        writeFileSync(resolve(refsDir, name), buf, 'utf-8')
      } catch {
        /* skip — onboarding proceeds without this one reference */
      }
    }),
    ...TEMPLATE_FILES.map(async (name) => {
      try {
        const buf = await fetchUrl(`${MIRROR_BASE}/assets/templates/${name}`)
        writeFileSync(resolve(templatesDir, name), buf, 'utf-8')
      } catch {
        /* skip — onboarding proceeds without this one template */
      }
    }),
  ])
}

function handleClaudeMd(cwd: string): 'created' | 'updated' | 'already configured' {
  const claudePath = resolve(cwd, 'CLAUDE.md')
  const reference = '@.driftless/skill.md'

  if (!existsSync(claudePath)) {
    writeFileSync(claudePath, `${reference}\n`, 'utf-8')
    return 'created'
  }

  const existing = readFileSync(claudePath, 'utf-8')
  if (existing.includes(reference)) {
    return 'already configured'
  }

  const needsNewline = existing.length > 0 && !existing.endsWith('\n')
  writeFileSync(claudePath, existing + (needsNewline ? '\n' : '') + `\n${reference}\n`, 'utf-8')
  return 'updated'
}

function handleAgentsMd(cwd: string): 'created' | 'updated' | 'already configured' {
  const agentsPath = resolve(cwd, 'AGENTS.md')

  if (!existsSync(agentsPath)) {
    writeFileSync(agentsPath, AGENTS_BLOCK, 'utf-8')
    return 'created'
  }

  const existing = readFileSync(agentsPath, 'utf-8')

  // Self-heal: replace the managed block in place so a stale block (e.g.
  // one written by an old CLI mentioning retired commands)
  // is refreshed on every run — instead of being frozen forever by a
  // skip-on-presence check.
  const s = existing.indexOf(DRIFTLESS_START)
  const e = existing.indexOf(DRIFTLESS_END)
  if (s !== -1 && e !== -1 && e > s) {
    const before = existing.slice(0, s)
    const after = existing.slice(e + DRIFTLESS_END.length)
    const next = before + AGENTS_BLOCK.trimEnd() + after
    if (next === existing) return 'already configured'
    writeFileSync(agentsPath, next, 'utf-8')
    return 'updated'
  }

  // Legacy migration: an old unmarked `## Driftless` block. It was always
  // appended last, so replace from that heading to EOF with the marked block.
  const legacy = existing.indexOf('## Driftless')
  if (legacy !== -1) {
    const before = existing.slice(0, legacy).replace(/\s+$/, '')
    writeFileSync(agentsPath, `${before}\n\n${AGENTS_BLOCK}`, 'utf-8')
    return 'updated'
  }

  const needsNewline = existing.length > 0 && !existing.endsWith('\n')
  writeFileSync(agentsPath, existing + (needsNewline ? '\n' : '') + '\n' + AGENTS_BLOCK, 'utf-8')
  return 'updated'
}

export type SkillStatus = 'created' | 'updated' | 'already configured'

export async function runInstallSkill(cwd: string): Promise<{
  claudeMd: SkillStatus
  agentsMd: SkillStatus
}> {
  await downloadSkill(cwd)
  const claudeMd = handleClaudeMd(cwd)
  const agentsMd = handleAgentsMd(cwd)
  return { claudeMd, agentsMd }
}

export async function installSkillCommand(): Promise<{
  claudeMd: SkillStatus
  agentsMd: SkillStatus
}> {
  const cwd = process.cwd()
  const result = await runInstallSkill(cwd)

  const icon = (s: SkillStatus) => s === 'created' ? 'created ✓' : s === 'updated' ? 'updated ✓' : 'already configured ✓'
  console.log(`  CLAUDE.md   ${icon(result.claudeMd)}`)
  console.log(`  AGENTS.md   ${icon(result.agentsMd)}`)

  // Install the context hooks by default (gated by the workspace toggle, so they
  // are inert until enabled at Settings → Automations): read-before (PreToolUse)
  // + write-after (PostToolUse observe + Stop reflection nudge).
  try {
    const { installPreEditHook, installPostEditHook, installStopHook } = await import('./hook')
    const added = [installPreEditHook(), installPostEditHook(), installStopHook()].some(Boolean)
    console.log(`  context hooks    ${added ? 'installed ✓ (OFF until you toggle Settings → Automations)' : 'already configured ✓'}`)
  } catch { /* best-effort */ }

  // Resolve workspace slug for analytics (best-effort)
  try {
    const { api } = await import('../api-client')
    const me = (await api.get('/me')) as { slug?: string }
    if (me?.slug) {
      const { analyticsEvent, flushAnalytics } = await import('../analytics')
      analyticsEvent('cli_install_skill', me.slug, { claude_md: result.claudeMd, agents_md: result.agentsMd })
      await flushAnalytics()
    }
  } catch { /* best-effort */ }

  return result
}
